import React, { FC, useEffect, useState } from 'react';
import { View, Text } from 'react-native';
import moment from 'moment';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from '@oolio-group/localization';
import { analyticsService } from '../../analytics/AnalyticsService';
import { Nav } from '../../common/types';
import { useSession } from '../../hooks/useSession';
import {
  LAST_SETTINGS_SYNCED_AT_KEY,
  getLastSettingsSyncedAt,
} from '../../state/preferences';
import { addSubscription, removeSubscription } from '../../storage/interface';
import ButtonIcon from '../../component/Buttons/ButtonIcon';
import { useSettingsSync } from './SettingsSyncContext';
import SettingsShellStyles from './SettingsScreen.styles';

// How often the "Synced X ago" label re-renders while Settings stays open.
const RELATIVE_TIME_REFRESH_MS = 30000;

const SettingsTopBar: FC = () => {
  const styles = SettingsShellStyles();
  const { translate } = useTranslation();
  const navigation = useNavigation<Nav>();
  const { session } = useSession();
  const { loading, fetchSettings } = useSettingsSync();

  const [lastSyncedAt, setLastSyncedAt] = useState<number | undefined>();
  // Re-renders the relative label on a timer without changing the timestamp.
  const [, setElapsedTick] = useState(0);

  // Subscribing to the storage key means the label updates when a sync actually
  // finishes writing, rather than racing the query's `loading` flag.
  useEffect(() => {
    let active = true;

    const applyTimestamp = (timestamp?: number) => {
      if (active) setLastSyncedAt(timestamp);
    };

    // The subscription can deliver a newer timestamp before this read resolves.
    // Applying it unconditionally would then move the label backwards, or back
    // to "Never synced", so only accept it if nothing newer has arrived.
    getLastSettingsSyncedAt().then(initial => {
      if (!active) return;
      setLastSyncedAt(current =>
        current !== undefined && (initial === undefined || initial < current)
          ? current
          : initial,
      );
    });
    addSubscription<number>(LAST_SETTINGS_SYNCED_AT_KEY, applyTimestamp);

    return () => {
      active = false;
      removeSubscription(LAST_SETTINGS_SYNCED_AT_KEY, applyTimestamp);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(
      () => setElapsedTick(tick => tick + 1),
      RELATIVE_TIME_REFRESH_MS,
    );

    return () => clearInterval(interval);
  }, []);

  const onPressBack = () => {
    if (session.authorized) {
      navigation.navigate('MainScreen');
    }
  };

  const onPressSync = () => {
    analyticsService.capture('Sync Requested');
    fetchSettings(true);
  };

  const syncLabel = lastSyncedAt
    ? translate('kitchenDisplay.syncedAgo', {
        time: moment(lastSyncedAt).fromNow(),
      })
    : translate('kitchenDisplay.neverSynced');

  return (
    <View style={styles.header}>
      <View style={styles.headerLeft}>
        <ButtonIcon
          ignoreTheme
          testID="btn-back"
          icon="arrow-left"
          type="interface"
          onPress={onPressBack}
        />
        <Text style={styles.headerTitle}>
          {translate('kitchenDisplay.settings')}
        </Text>
        <View testID="device-badge" style={styles.deviceBadge}>
          <Text style={styles.deviceBadgeText}>
            {session?.kitchenDisplay?.name}
          </Text>
        </View>
      </View>
      <View style={styles.headerRight}>
        <View testID="sync-status">
          <Text style={styles.syncText}>{syncLabel}</Text>
        </View>
        <ButtonIcon
          ignoreTheme
          icon="sync"
          testID="btn-sync"
          type="interface"
          loading={loading}
          onPress={onPressSync}
        />
      </View>
    </View>
  );
};

export default SettingsTopBar;
